import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  List, ListItem, ListItemText, Divider, Chip, Grid, Pagination,
  Accordion, AccordionSummary, AccordionDetails, Checkbox, useTheme,
  FormControl, Select, MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { searchSenateDivisions } from '../services/api';
import SearchSkeleton from './SearchSkeleton';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import DateHierarchyFilter from './DateHierarchyFilter';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';
import ExportCSVButton from './ExportCSVButton';
import { SaveSearchButton } from './saved-searches';
import RSSButton from './RSSButton';
import { chamberColor } from './explore/charts';

// Explicit facet order (CLAUDE.md search-page standards) — never insertion order.
// `years` is consumed by <DateHierarchyFilter>, which renders after this list.
const FACET_ORDER = ['passed'];

// Primary discriminating filter for divisions is the outcome; everything else
// stays collapsed.
const DEFAULT_EXPANDED_FACETS = new Set(['passed']);

// Sort vocabulary — identical to the House divisions page. Both endpoints now
// accept these three tokens, so the user gets the same choice on either page.
// Relevance is only meaningful with a real query, so it is offered only when one
// is present.
const SORT_NEWEST = 'date_desc';
const SORT_OLDEST = 'date_asc';
const SORT_RELEVANCE = '_score';
const DATE_SORT_OPTIONS = [
  { value: SORT_NEWEST, label: 'Date (newest)' },
  { value: SORT_OLDEST, label: 'Date (oldest)' },
];
const RELEVANCE_SORT_OPTION = { value: SORT_RELEVANCE, label: 'Relevance' };

// The API returns facets either as [{value, count}] or {buckets: [{key, doc_count}]}
// depending on the endpoint's vintage. Normalise both.
function facetBuckets(facet) {
  if (!facet) return [];
  const raw = Array.isArray(facet) ? facet : (facet.buckets || []);
  return raw
    .map((b) => ({
      value: b.value !== undefined ? b.value : b.key,
      count: b.count !== undefined ? b.count : b.doc_count,
    }))
    .filter((b) => b.value !== undefined && b.value !== null);
}

// `passed` is a boolean in ES — query it with true/false, never the facet's
// display label or a 1/0.
function passedQueryValue(value) {
  return (value === 1 || value === true || value === '1' || value === 'true' || value === 'Passed')
    ? 'true'
    : 'false';
}

function passedLabel(value) {
  return passedQueryValue(value) === 'true' ? 'Passed' : 'Not passed';
}

// Division titles arrive as "2026-05-14 Division 1: Second Reading"; the date and
// division number are shown separately, so strip that prefix for the headline.
const TITLE_PREFIX_RE = /^\s*\d{4}-\d{2}-\d{2}\s+Division\s+\d+\s*:\s*/i;

function divisionSubject(hit) {
  // `question` is a House-only enrichment; handled here too so the two pages
  // stay symmetrical if it ever lands on Senate docs.
  const question = hit.question && String(hit.question).trim();
  if (question) return question;
  const raw = (hit.title || '').trim();
  const stripped = raw.replace(TITLE_PREFIX_RE, '').trim();
  return stripped || raw || 'Untitled division';
}

// Keep a source link only when it points at the APH family. TheyVoteForYou was
// retired from this project, so never label or link it.
function parlInfoUrl(hit) {
  if (!hit.source_url) return null;
  try {
    const host = new URL(hit.source_url).hostname.toLowerCase();
    return (host === 'aph.gov.au' || host.endsWith('.aph.gov.au')) ? hit.source_url : null;
  } catch (e) {
    return null;
  }
}

/**
 * The tally is the single most important fact about a division, so it is the
 * dominant element of the card: large tabular figures plus a proportional bar
 * so a close division and a lopsided one look different at a glance.
 */
function TallyBar({ ayes, noes, absent }) {
  const theme = useTheme();
  const t = theme.palette.ledger;
  const a = Number(ayes) || 0;
  const n = Number(noes) || 0;
  const cast = a + n;
  const ayePct = cast > 0 ? (a / cast) * 100 : 50;
  const margin = Math.abs(a - n);

  const figure = (label, value, color) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
      <Typography variant="body2" sx={{ color: t.muted }}>{label}</Typography>
      <Typography
        component="span"
        sx={{ fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.1, color, fontVariantNumeric: 'tabular-nums' }}
      >
        {value.toLocaleString()}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{ mt: 1.5, maxWidth: 520 }}
      role="group"
      aria-label={`Ayes ${a}, noes ${n}${absent ? `, absent ${absent}` : ''}`}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 3, flexWrap: 'wrap', mb: 0.75 }}>
        {figure('Ayes', a, theme.palette.success.main)}
        {figure('Noes', n, theme.palette.error.main)}
        {absent > 0 && (
          <Typography variant="body2" sx={{ color: t.faint, fontVariantNumeric: 'tabular-nums' }}>
            Absent {Number(absent).toLocaleString()}
          </Typography>
        )}
        {cast > 0 && (
          <Typography variant="body2" sx={{ color: t.faint, fontVariantNumeric: 'tabular-nums' }}>
            {margin === 0 ? 'Tied' : `Majority ${margin.toLocaleString()}`}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          height: 10,
          borderRadius: 0.5,
          overflow: 'hidden',
          bgcolor: t.hover,
          border: `${t.ruleWidth} solid ${t.ruleColor}`,
        }}
      >
        <Box sx={{ width: `${ayePct}%`, bgcolor: theme.palette.success.main }} />
        <Box sx={{ width: `${100 - ayePct}%`, bgcolor: theme.palette.error.main }} />
        {/* Halfway marker — makes a near-tie legible without reading the numbers. */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: 0,
            bottom: 0,
            width: t.ruleWidth,
            bgcolor: t.surface,
            opacity: 0.9,
          }}
        />
      </Box>
    </Box>
  );
}

export default function SenateDivisionsSearch() {
  const theme = useTheme();
  const ledger = theme.palette.ledger;
  const senateColor = chamberColor('Senate');
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  const [sortOrder, setSortOrder] = useState(() => {
    const urlSort = searchParams.get('sort');
    return [SORT_NEWEST, SORT_OLDEST, SORT_RELEVANCE].includes(urlSort) ? urlSort : SORT_NEWEST;
  });
  const [facets, setFacets] = useState({});
  const [activeFilters, setActiveFilters] = useState({});

  // Date drill-down state
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        const fieldQuery = values.map(v => `${field}:${v}`).join(' OR ');
        filterParts.push(`(${fieldQuery})`);
      }
    });
    if (filterParts.length === 0) return baseQuery;
    if (baseQuery === '*') return filterParts.join(' AND ');
    return `(${baseQuery}) AND ${filterParts.join(' AND ')}`;
  };

  const handleSearch = async (searchQuery = null, newPage = 1, filters = activeFilters, year = selectedYear, month = selectedMonth, pageSize = perPage, sortBy = sortOrder) => {
    const baseQuery = searchQuery !== null ? searchQuery : (query || '*');

    // The outcome filter travels as the dedicated `passed` param instead of
    // being folded into `q`: a filter buried in the query string cannot be
    // excluded from its own facet, which is what makes the Result counts
    // corpus-wide. Both boxes ticked is the same as neither.
    const { passed: passedValues, ...queryFilters } = filters;
    const passedParam = (passedValues && passedValues.length === 1)
      ? passedValues[0] === 'true'
      : null;

    const q = buildQueryWithFilters(baseQuery, queryFilters);
    // Relevance ranks nothing useful when the query is "everything".
    const effectiveSort = (sortBy === SORT_RELEVANCE && (!baseQuery || baseQuery === '*'))
      ? SORT_NEWEST
      : sortBy;

    setLoading(true);
    setError(null);
    try {
      const data = await searchSenateDivisions(q, newPage, pageSize, passedParam, year, month, effectiveSort);
      setResults(data);
      setPage(newPage);
      if (data.facets) setFacets(data.facets);
      const newParams = { q: baseQuery };
      if (pageSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = pageSize;
      if (effectiveSort !== SORT_NEWEST) newParams.sort = effectiveSort;
      setSearchParams(newParams);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search. Please try again.');
      setResults(null);
    }
    setLoading(false);
    setInitialLoading(false);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(null, 1, activeFilters, selectedYear, selectedMonth, newSize);
  };

  const handleSortChange = (newSort) => {
    setSortOrder(newSort);
    handleSearch(null, 1, activeFilters, selectedYear, selectedMonth, perPage, newSort);
  };

  // Date drill-down handlers
  const handleYearClick = (year) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, year, null);
  };

  const handleMonthClick = (month) => {
    setSelectedMonth(month);
    setSelectedDay(null);
    const year = month.split('-')[0];
    setSelectedYear(year);
    handleSearch(null, 1, activeFilters, year, month);
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
  };

  const clearDateDrillDown = () => {
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, null, null);
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) newFilters[field] = [];
    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) delete newFilters[field];
    } else {
      newFilters[field].push(value);
    }
    setActiveFilters(newFilters);
    handleSearch(null, 1, newFilters);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(null, 1, {});
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(null, newPage);
  };

  const handleReset = () => {
    setQuery('');
    setResults(null);
    setPage(1);
    setSortOrder(SORT_NEWEST);
    setActiveFilters({});
    setFacets({});
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    setSearchParams({});
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const facetDisplayName = (facetName) => {
    const names = { passed: 'Result', years: 'Year' };
    return names[facetName] || facetName;
  };

  // Relevance is offered only once there is something to be relevant to.
  const hasQueryText = (query || '').trim() !== '' && (query || '').trim() !== '*';
  const sortOptions = hasQueryText ? [...DATE_SORT_OPTIONS, RELEVANCE_SORT_OPTION] : DATE_SORT_OPTIONS;
  const sortValue = sortOptions.some((o) => o.value === sortOrder) ? sortOrder : SORT_NEWEST;

  const hasFacets = FACET_ORDER.some((name) => facetBuckets(facets[name]).length > 0)
    || facets.years?.length > 0
    || facets.months?.length > 0
    || facets.days?.length > 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        {/* Chamber convention (charts.js chamberColor): Senate red. Used only as
            the chamber indicator, never as panel or chrome colour. */}
        <HowToVoteIcon sx={{ color: senateColor, fontSize: 32 }} />
        <Typography variant="h4">Senate Divisions</Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            sx={{ flex: '1 1 260px' }}
            placeholder="Search Senate divisions by question or senator..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            variant="outlined"
          />
          <Button
            variant="contained"
            onClick={() => handleSearch()}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            sx={{ px: 4 }}
          >
            Search
          </Button>
          <Button variant="outlined" onClick={handleReset}>
            Reset
          </Button>
        </Box>
      </Paper>

      {initialLoading && <SearchSkeleton count={5} showFacets={true} />}

      {!initialLoading && error && (
        <ErrorState message={error} onRetry={() => handleSearch()} />
      )}

      {!initialLoading && !error && results && results.total === 0 && (
        <EmptyState
          query={query}
          type="Senate divisions"
          onSuggestionClick={(q) => { setQuery(q); handleSearch(q); }}
          onReset={handleReset}
        />
      )}

      {!initialLoading && !error && results && results.total > 0 && (
        <Grid container spacing={3}>
          {/* Filter rail — scrolls with the page (no sticky / maxHeight / overflow). */}
          {hasFacets && (
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>Filter results</Typography>
                  {(Object.keys(activeFilters).length > 0 || selectedYear) && (
                    <Button size="small" onClick={() => { clearAllFilters(); clearDateDrillDown(); }}>Clear all</Button>
                  )}
                </Box>

                {FACET_ORDER.map((facetName) => {
                  const buckets = facetBuckets(facets[facetName]);
                  if (buckets.length === 0) return null;
                  return (
                    <Accordion key={facetName} defaultExpanded={DEFAULT_EXPANDED_FACETS.has(facetName)}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">{facetDisplayName(facetName)}</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <List dense sx={{ py: 0 }}>
                          {buckets.map((bucket) => {
                            const queryValue = passedQueryValue(bucket.value);
                            return (
                              <ListItem key={String(bucket.value)} disablePadding>
                                <Checkbox
                                  size="small"
                                  checked={activeFilters[facetName]?.includes(queryValue) || false}
                                  onChange={() => handleFacetClick(facetName, queryValue)}
                                />
                                <ListItemText
                                  primary={`${passedLabel(bucket.value)} (${Number(bucket.count || 0).toLocaleString()})`}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}

                {/* Date Hierarchy Filter */}
                {(facets.years?.length > 0 || facets.months?.length > 0 || facets.days?.length > 0) && (
                  <DateHierarchyFilter
                    facets={facets}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    selectedDay={selectedDay}
                    onYearClick={handleYearClick}
                    onMonthClick={handleMonthClick}
                    onDayClick={handleDayClick}
                    onClearAll={clearDateDrillDown}
                  />
                )}
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} md={hasFacets ? 9 : 12}>
            <Paper sx={{ p: 2 }}>
              {/* Std results layout (CLAUDE.md). Row 1: count (left) + pagination (right). */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1">
                  {results.total.toLocaleString()} {results.total === 1 ? 'result' : 'results'} found
                </Typography>
                {results.total > perPage && (
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
              {/* Row 2 (CLAUDE.md order): Per page · Sort by · Export · Save search · RSS. */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">Sort by</Typography>
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <Select
                      value={sortValue}
                      onChange={(e) => handleSortChange(e.target.value)}
                      inputProps={{ 'aria-label': 'Sort results' }}
                      sx={{ '& .MuiSelect-select': { py: 0.5 }, fontSize: '0.875rem' }}
                    >
                      {sortOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <ExportCSVButton
                  dataType="senate-divisions"
                  query={query || '*'}
                  totalResults={results.total}
                />
                <SaveSearchButton
                  searchType="senate_divisions"
                  queryText={query || '*'}
                  parameters={{ page, per_page: perPage, ...activeFilters }}
                  resultCount={results.total}
                />
                <RSSButton collection="senate-divisions" query={query || '*'} />
              </Box>

              <Box>
                {results.hits.map((hit, index) => {
                  const subject = divisionSubject(hit);
                  const sourceUrl = parlInfoUrl(hit);
                  const absent = Number(
                    hit.absent_count !== undefined ? hit.absent_count : (hit.absent_voters?.length || 0)
                  );
                  const meta = [
                    formatDate(hit.date),
                    hit.house || 'Senate',
                    hit.division_number ? `Division ${hit.division_number}` : null,
                  ].filter(Boolean).join(' · ');
                  return (
                    <React.Fragment key={hit.id}>
                      {index > 0 && <Divider />}
                      <Box sx={{ py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <Typography
                            variant="resultTitle"
                            component={RouterLink}
                            to={`/senate-divisions/${encodeURIComponent(hit.id)}`}
                            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {subject}
                          </Typography>
                          <Chip
                            label={hit.passed ? 'Passed' : 'Not passed'}
                            size="small"
                            color={hit.passed ? 'success' : 'error'}
                          />
                        </Box>

                        <Typography variant="body2" sx={{ mt: 0.5, color: ledger.muted }}>
                          {meta}
                        </Typography>

                        {hit.mover?.name && (
                          <Typography variant="body2" sx={{ mt: 0.5, color: ledger.muted }}>
                            Moved by{' '}
                            {hit.mover.phid ? (
                              <RouterLink
                                to={`/parliamentarians/${hit.mover.phid}`}
                                style={{ color: ledger.accentText, textDecoration: 'none' }}
                              >
                                {hit.mover.name}
                              </RouterLink>
                            ) : hit.mover.name}
                          </Typography>
                        )}

                        {hit.bill_linked?.id && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            Bill:{' '}
                            <RouterLink
                              to={`/bills/${hit.bill_linked.id}`}
                              style={{ color: ledger.accentText, textDecoration: 'none' }}
                            >
                              {hit.bill_linked.title}
                            </RouterLink>
                          </Typography>
                        )}

                        <TallyBar ayes={hit.aye_count} noes={hit.no_count} absent={absent} />

                        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Button
                            component={RouterLink}
                            to={`/senate-divisions/${encodeURIComponent(hit.id)}`}
                            size="small"
                            endIcon={<ChevronRightIcon />}
                          >
                            View division
                          </Button>
                          {sourceUrl && (
                            <Button
                              size="small"
                              variant="outlined"
                              href={sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View on ParlInfo
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </React.Fragment>
                  );
                })}
              </Box>

              {results.total > perPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
